/**
 * Central language registry — mirror of /app/backend/app/languages.py.
 *
 * Why a mirror?
 * - The provider needs to know `fallback_chain` and `is_active` synchronously
 *   on first render (before any /api/languages fetch could complete).
 * - The backend file is the source of truth; this file MUST be kept in
 *   sync when a language is activated. If they ever drift, the runtime
 *   simply falls back to EN — there is no security or data integrity risk.
 *
 * Adding a new language:
 * 1. Mark `isActive: true` here AND in `/app/backend/app/languages.py`.
 * 2. Add the dictionary key for that code in `translations.js`.
 * 3. (Optional) supply `label_{code}` fields in questionnaire imports.
 *
 * Phase A4 (Feb 2026):
 * - Architecture extended from 11 → 15 registered languages.
 * - CS / NL / AR / HE registered as `isActive: false` and
 *   `reviewStatus: "pending_review"`. They MUST NOT appear in the public
 *   language switcher until manually approved (and matching translations
 *   are shipped). `LanguageDropdown` already filters by `isActive`, so
 *   no UI change is needed to keep them hidden.
 * - `dir: "ltr" | "rtl"` declared per entry. The provider applies
 *   `<html dir>` when an RTL language is selected, allowing the layout
 *   to flip safely once AR/HE are activated. Non-RTL languages remain
 *   LTR exactly as today.
 */

export const LANGUAGES = [
  // Active today
  { code: "en",    nameEn: "English",              nameNative: "English",               isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["en"] },
  { code: "es",    nameEn: "Spanish",              nameNative: "Español",               isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["es", "en"] },
  { code: "zh",    nameEn: "Simplified Chinese",   nameNative: "简体中文",              isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["zh", "en"] },
  // Phase-4 rollout (May 2026) — UI shell translated, questionnaire
  // content gracefully falls back to EN until professional translations
  // are imported via the Questionnaire Import Center.
  { code: "it",    nameEn: "Italian",              nameNative: "Italiano",              isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["it", "en"] },
  { code: "fr",    nameEn: "French",               nameNative: "Français",              isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["fr", "en"] },
  { code: "de",    nameEn: "German",               nameNative: "Deutsch",               isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["de", "en"] },
  { code: "pt-br", nameEn: "Brazilian Portuguese", nameNative: "Português do Brasil",   isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["pt-br", "en"] },
  { code: "ja",    nameEn: "Japanese",             nameNative: "日本語",                isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["ja", "en"] },
  { code: "hi",    nameEn: "Hindi",                nameNative: "हिन्दी",                  isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["hi", "en"] },
  { code: "ko",    nameEn: "Korean",               nameNative: "한국어",                isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["ko", "en"] },
  { code: "ru",    nameEn: "Russian",              nameNative: "Русский",               isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["ru", "en"] },
  // Phase A4 — registered architecture only. Inactive + pending_review.
  // Hidden from the public language switcher; admin-only audit surface.
  // No translation work begins until each entry is approved.
  { code: "cs",    nameEn: "Czech",                nameNative: "Čeština",               isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["cs", "en"] },
  { code: "nl",    nameEn: "Dutch",                nameNative: "Nederlands",            isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["nl", "en"] },
  { code: "ar",    nameEn: "Arabic",               nameNative: "العربية",                isActive: true,  dir: "rtl", reviewStatus: "approved", fallback: ["ar", "en"] },
  { code: "he",    nameEn: "Hebrew",               nameNative: "עברית",                  isActive: true,  dir: "rtl", reviewStatus: "approved", fallback: ["he", "en"] },
  // Iberian regional languages (Feb 2026) — Roberto's roadmap extension
  // from 15 → 18 registered languages. Architecture-only placeholders.
  // NOT publicly active. Hidden from `LanguageDropdown` (filters by
  // `isActive`). NO translation work begins until manually approved.
  // Fallback chain prefers ES (closest regional fit) before EN so any
  // partial translation reads naturally to a CA/EU/GL speaker.
  { code: "ca",    nameEn: "Catalan",              nameNative: "Català",                isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["ca", "es", "en"] },
  { code: "eu",    nameEn: "Basque",               nameNative: "Euskara",               isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["eu", "es", "en"] },
  { code: "gl",    nameEn: "Galician",             nameNative: "Galego",                isActive: true,  dir: "ltr", reviewStatus: "approved", fallback: ["gl", "es", "en"] },
];

export const ACTIVE_CODES = LANGUAGES.filter((l) => l.isActive).map((l) => l.code);
export const REGISTERED_CODES = LANGUAGES.map((l) => l.code);
export const RTL_CODES = LANGUAGES.filter((l) => l.dir === "rtl").map((l) => l.code);

const BY_CODE = Object.fromEntries(LANGUAGES.map((l) => [l.code, l]));

export const fallbackChain = (code) => {
  const entry = BY_CODE[code];
  return entry ? entry.fallback : ["en"];
};

export const isActiveLang = (code) => !!BY_CODE[code]?.isActive;
export const getLang = (code) => BY_CODE[code] || null;
// Phase A4: language direction lookup. Defaults to "ltr" for any unknown
// or inactive code so the rest of the app never accidentally enters
// RTL mode unless the active language explicitly declares `dir: "rtl"`.
export const dirOf = (code) => BY_CODE[code]?.dir || "ltr";
export const isRTL = (code) => dirOf(code) === "rtl";
